import { acceptHMRUpdate, defineStore } from 'pinia'
import { useDrawingStore } from './drawing'
import { useMaps } from './maps'
import { toRaw } from 'vue'

export const BULK_RESULT = {
    ABORTED: 'Aborted',
    UPLOAD_FAILED: 'Upload failed',
    SAVED: 'Saved',
    UNEXPECTED_ERROR: 'Unexpected error',
    BORDER_PROBLEMS: 'Border problems',
    BORDER_CORRECT_LIMIT: 'Border correction limit reached',
    HEADLAND_FAILED: 'Headland failed',
    AB_LINES_FAILED: 'AB lines failed',
    NO_AB_OPTION: 'No AB option',
    INVALID_GEOMETRY: 'Invalid geometry',
}

export function getFieldDisplayName(field, index) {
    const props = field?.outer_border?.features?.find(
        f => f.properties?.field_geom_type === 'outer_border'
    )?.properties
    const fieldName = String(props?.field_name ?? '').trim()
    if (fieldName) return fieldName

    const schlagFbi = [props?.schlag, props?.fbi].filter(Boolean).join(' ').trim()
    if (schlagFbi) return schlagFbi

    return `Field ${index + 1}`
}

export const useUploadMultipleBordersStore = defineStore('uploadMultipleBorders', {
    state: () => ({
        availableFields: [],
        sourceFileName: '',
        currentFieldIndex: 0,
        hasMultipleFiles: false,
        selectedField: null,
        selectedPreviewShapes: {},
        loading: false,
        bulkInProgress: false,
        bulkUploadState: {
            name: '',
            operation: ''
        },
        bulkAborted: false,
        bulkError: '',
        bulkResults: [],
        bulkResultId: 0,
        selectedBulkIndexes: [],
        bulkRunIndex: 0,
        bulkRunTotal: 0,
    }),
    getters: {
        bulkFieldOptions: (state) => state.availableFields.map((field, index) => ({
            label: getFieldDisplayName(field, index),
            value: index
        }))
    },
    actions: {
        drawFieldPreview(field, color = '#ff0000') {
            const mapStore = useMaps()
            const drawingStore = useDrawingStore()
            const outerBorder = field?.outer_border?.features?.find(f => f.properties.field_geom_type === 'outer_border')
            if (!outerBorder?.geometry?.coordinates) return null

            const coordinates = outerBorder.geometry.coordinates[0].map(coord => ({
                lat: coord[1],
                lng: coord[0]
            }))

            const polygon = new mapStore.google.maps.Polygon({
                paths: coordinates,
                editable: false,
                draggable: false,
                clickable: false,
                strokeColor: color,
                strokeWeight: 3,
                fillColor: color,
                fillOpacity: 0.1,
                zIndex: 1
            })
            toRaw(polygon).setMap(mapStore.map)

            const obstacles = []
            if (outerBorder.geometry.coordinates.length > 1) {
                for (let i = 1; i < outerBorder.geometry.coordinates.length; i++) {
                    const obstacleCoords = outerBorder.geometry.coordinates[i].map(coord => ({
                        lat: coord[1],
                        lng: coord[0]
                    }))
                    const obstacle = new mapStore.google.maps.Polygon({
                        paths: drawingStore.rewindRing(obstacleCoords, false),
                        editable: false,
                        draggable: false,
                        clickable: false,
                        strokeColor: color,
                        strokeWeight: 2,
                        fillColor: 'transparent',
                        fillOpacity: 0,
                        zIndex: 2
                    })
                    obstacle.setMap(mapStore.map)
                    obstacles.push(obstacle)
                }
            }

            const bounds = new mapStore.google.maps.LatLngBounds()
            coordinates.forEach(coord => bounds.extend(coord))

            return { polygon, obstacles, bounds }
        },
        setSelectedBulkIndexes(indexes) {
            this.selectedBulkIndexes = indexes
            this.syncSelectedPreviews()
        },
        syncSelectedPreviews() {
            const selected = new Set(this.selectedBulkIndexes)
            Object.keys(this.selectedPreviewShapes).forEach(key => {
                const i = Number(key)
                if (!selected.has(i)) this.removeSelectedPreview(i)
            })

            let added = false
            selected.forEach(i => {
                if (this.selectedPreviewShapes[i]) return
                const drawn = this.drawFieldPreview(this.availableFields[i])
                if (!drawn) return
                this.selectedPreviewShapes[i] = drawn
                added = true
            })

            if (added) this.fitSelectedPreviews()
        },
        removeSelectedPreview(index) {
            const drawn = this.selectedPreviewShapes[index]
            if (!drawn) return
            toRaw(drawn.polygon).setMap(null)
            drawn.obstacles.forEach(obstacle => toRaw(obstacle).setMap(null))
            delete this.selectedPreviewShapes[index]
        },
        fitSelectedPreviews() {
            const mapStore = useMaps()
            const bounds = new mapStore.google.maps.LatLngBounds()
            Object.values(this.selectedPreviewShapes).forEach(drawn => {
                if (drawn.bounds) bounds.union(toRaw(drawn.bounds))
            })
            if (!bounds.isEmpty()) mapStore.map.fitBounds(bounds, { padding: 50 })
        },
        reset() {
            this.availableFields = []
            this.sourceFileName = ''
            this.currentFieldIndex = 0
            this.hasMultipleFiles = false
            this.loading = false
            this.bulkInProgress = false
            this.selectedField = null
            this.bulkAborted = false
            this.bulkError = ''
            this.bulkResults = []
            this.bulkUploadState = { name: '', operation: '' }
            this.selectedBulkIndexes = []
            this.bulkRunIndex = 0
            this.bulkRunTotal = 0
            this.clearPreview()
        },
        clearPreview() {
            Object.keys(this.selectedPreviewShapes).forEach(key => this.removeSelectedPreview(Number(key)))
        },
        async selectMultipleFields() {
            const indexes = [...this.selectedBulkIndexes].sort((a, b) => a - b)
            if (!indexes.length) return false
            return await this.runBulkUpload(indexes.map(i => this.availableFields[i]).filter(Boolean))
        },
        async runBulkUpload(fields) {
            const ds = useDrawingStore()

            this.bulkError = ''
            if (!ds.selectedCustomer?.id) {
                this.bulkError = 'Please select a customer for the field planning'
                return false
            }

            this.loading = true
            this.bulkInProgress = true
            ds.turnOffEditablePolygon()
            ds.disablePolygon()
            this.clearPreview()
            try {
                await this.uploadBulkFields(fields)
                await ds.clearDrawing()
                const mapStore = useMaps()
                await mapStore.getAllFields(false)
            } finally {
                this.bulkInProgress = false
                this.loading = false
            }
            return true
        },
        async uploadBulkFields(fields) {
            const list = fields?.length ? fields : [...this.availableFields]
            this.bulkAborted = false
            this.bulkResults = []
            this.bulkRunTotal = list.length

            for (let i = 0; i < list.length; i++) {
                const originalIndex = this.availableFields.indexOf(list[i])
                const idx = originalIndex >= 0 ? originalIndex : i

                if (this.bulkAborted) {
                    for (let j = i; j < list.length; j++) {
                        const abortIdx = this.availableFields.indexOf(list[j])
                        this.addBulkResult(
                            this.getBulkFieldName(list[j], abortIdx >= 0 ? abortIdx : j),
                            false,
                            BULK_RESULT.ABORTED,
                            abortIdx >= 0 ? abortIdx : j
                        )
                    }
                    break
                }

                this.bulkRunIndex = i
                this.currentFieldIndex = idx
                this.selectedField = list[i]
                try {
                    await this.uploadBulkField(list[i], idx)
                } catch (error) {
                    const name = this.getBulkFieldName(list[i], idx)
                    this.addBulkResult(name, false, error?.message || BULK_RESULT.UNEXPECTED_ERROR)
                    console.warn('Bulk field failed', { index: idx, error })
                }
            }

            this.bulkUploadState = { name: '', operation: '' }
        },
        setBulkUploadState(name, operation) {
            this.bulkUploadState = { name, operation }
        },
        addBulkResult(name, ok, reason, index = this.currentFieldIndex) {
            this.bulkResults.unshift({
                id: ++this.bulkResultId,
                name,
                ok,
                reason,
                current: index + 1,
                total: this.availableFields.length
            })
        },
        abortBulkUpload() {
            this.bulkAborted = true
            this.setBulkUploadState(this.bulkUploadState.name, 'Aborting...')
        },
        getBulkFieldName(field, index) {
            return getFieldDisplayName(field, index)
        },
        isBulkAborted(fieldName) {
            if (!this.bulkAborted) return false
            this.addBulkResult(fieldName, false, BULK_RESULT.ABORTED)
            return true
        },
        async uploadBulkField(field, index) {
            const ds = useDrawingStore()
            const name = this.getBulkFieldName(field, index)
            this.setBulkUploadState(name, 'Loading field')

            const result = await ds.uploadFieldFile(null, false, field)
            if (this.isBulkAborted(name)) return

            if (result === false || result === 'field_null') {
                this.addBulkResult(name, false, BULK_RESULT.UPLOAD_FAILED)
                return
            }

            if (!ds.field.name?.trim()) {
                ds.field.name = name
            }
            const fieldName = ds.field.name

            if (ds.selectedCustomer?.id) {
                this.setBulkUploadState(fieldName, 'Applying farm headland config')
                await ds.toogleHeadlandConfig()
                if (this.isBulkAborted(fieldName)) return
            }

            this.setBulkUploadState(fieldName, 'Generating headland')
            const corrected = await ds.autoCorrectBulkBorderProblems()
            if (this.isBulkAborted(fieldName)) return
            if (corrected === 'headland_failed') {
                this.addBulkResult(fieldName, false, BULK_RESULT.HEADLAND_FAILED)
                return
            }
            if (corrected === 'max_attempts') {
                this.addBulkResult(fieldName, false, BULK_RESULT.BORDER_CORRECT_LIMIT)
                return
            }
            if (!corrected) {
                this.addBulkResult(fieldName, false, BULK_RESULT.BORDER_PROBLEMS)
                return
            }

            this.setBulkUploadState(fieldName, 'Generating AB lines')
            const abOk = await ds.optimizeLongestEdge()
            if (this.isBulkAborted(fieldName)) return

            ds.selectFewestABLinesOption()
            if (!abOk || !ds.field.selectedABOption || !ds.hasAnyValidABLinesOption(ds.field.abLinesOptions)) {
                this.addBulkResult(fieldName, false, BULK_RESULT.NO_AB_OPTION)
                return
            }

            if (ds.checkFieldValidity() !== null) {
                this.addBulkResult(fieldName, false, BULK_RESULT.INVALID_GEOMETRY)
                return
            }

            this.setBulkUploadState(fieldName, 'Saving')

            const res = await ds.saveFieldToDB(false)
            if (this.isBulkAborted(fieldName)) return
            if (!res?.ok) {
                this.addBulkResult(fieldName, false, res?.reason || BULK_RESULT.UNEXPECTED_ERROR)
                return
            }
            this.addBulkResult(fieldName, true, BULK_RESULT.SAVED)
        },
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUploadMultipleBordersStore, import.meta.hot))
}
